import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TooltipDirective } from '../../../../../shared/components/ui/tooltip/tooltip.directive';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { faStar, faXmark } from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { PasswordConfirmInputs } from '../../../../../shared/components/form-fields/password-confirm-inputs/password-confirm-inputs';
import { Notice } from '../../../../../shared/components/ui/notice/notice';

export interface TransferDefaultDialogData {
  newDefaultName: string;
  oldDefaultName: string;
}

/** `undefined` on cancel/backdrop dismiss. */
export type TransferDefaultDialogResult = { password: string } | { delete: true } | undefined;

/**
 * Confirmation form for transferring the default role. The default role is
 * what every visitor gets without signing in, so the transfer needs a
 * disposition for the previously-default role: a real password (its members
 * are signed out and must re-authenticate) or deletion. Pure form — the
 * parent calls `AppService.transferDefaultRole` with the result. Built on the
 * same `MatDialog` pattern as `ConfirmDialog`.
 */
@Component({
  selector: 'andor-transfer-default-dialog',
  standalone: true,
  imports: [TranslatePipe, TooltipDirective, FaDuotoneIconComponent, PasswordConfirmInputs, Notice],
  templateUrl: './transfer-default-dialog.html',
  styleUrl: './transfer-default-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransferDefaultDialog {
  private readonly _dialogRef =
    inject<MatDialogRef<TransferDefaultDialog, TransferDefaultDialogResult>>(MatDialogRef);
  protected readonly data = inject<TransferDefaultDialogData>(MAT_DIALOG_DATA);

  protected readonly faStar = faStar;
  protected readonly faXmark = faXmark;

  mode = signal<'password' | 'delete'>('password');
  password = signal('');
  passwordValid = signal(false);

  protected canConfirm(): boolean {
    return this.mode() === 'delete' || this.passwordValid();
  }

  protected confirm(): void {
    if (this.mode() === 'delete') {
      this._dialogRef.close({ delete: true });
    } else if (this.passwordValid()) {
      this._dialogRef.close({ password: this.password() });
    }
  }

  protected cancel(): void {
    this._dialogRef.close(undefined);
  }
}
